import _merge from 'lodash/merge';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import { Lightning } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { sendEvent } from '../../../../logic/analytics/analytics.utils';
import { TOP_BAR_RELATED_VIDEOS, TOP_BAR_INFO_PAGE, TOP_BAR_TRACKS } from '../../../../logic/player/player.constants';
import { SELECTED_SIGNAL } from '../../../tornado/signals';
import { Overlay } from '../overlay/overlay.component';
import { TopBar } from '../topBar/topBar.component';
import { Toggle } from '../eventMarker/eventMarker.component';
import { Metadata } from '../metadata/metadata.component';
import { SideGlass } from '../sideGlass/sideGlass.component';
import { CLOSE_SIGNAL as SIDE_GLASS_CLOSE_SIGNAL } from '../sideGlass/sideGlass.constants';
import { Tracks } from '../tracks/tracks.component';
import {
  CLOSE_SIGNAL as TRACKS_CLOSE_SIGNAL,
  AUDIO_TRACK_SELECTED_SIGNAL,
  SUBTITLES_TRACK_SELECTED_SIGNAL,
} from '../tracks/tracks.constants';
import { ProgressBar } from '../progressBar/progressBar.component';
import { PAUSE_SIGNAL, SEEK_SIGNAL } from '../progressBar/progressBar.constants';
import { INTERFACE_DISPLAY_DURATION } from '../../player.constants';
import { ProgramInformation } from '../programInformation/programInformation.component';
import { PROGRAM_INFORMATION_CLOSED_SIGNAL } from '../programInformation/programInformation.constants';
import connect from './controls.connector';
import { getInitialStyle } from './controls.style';
import {
  TOP_BAR_ITEM_SELECTED_SIGNAL,
  SIDE_GLASS_ITEM_SELECTED_SIGNAL,
  CONTROLS_HIDE_SIGNAL,
  CONTROLS_SHOW_SIGNAL,
} from './controls.constants';

export class Controls extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Main: {
          Overlay: {
            type: Overlay,
          },
          TopBar: {
            type: TopBar,
            signals: {
              [SELECTED_SIGNAL]: '_handleTopBarItemSelected',
            },
          },
          Toggle: {
            type: Toggle,
          },
          Metadata: {
            type: Metadata,
          },
          Progress: {
            type: ProgressBar,
            passSignals: {
              [SEEK_SIGNAL]: true,
              [PAUSE_SIGNAL]: true,
            },
          },
          SideGlass: {
            type: SideGlass,
            passSignals: {
              [SELECTED_SIGNAL]: SIDE_GLASS_ITEM_SELECTED_SIGNAL,
            },
            signals: {
              [SIDE_GLASS_CLOSE_SIGNAL]: '_handleSideGlassClose',
            },
          },
          Tracks: {
            type: Tracks,
            signals: {
              [TRACKS_CLOSE_SIGNAL]: '_handleTracksPanelClose',
            },
            passSignals: {
              [AUDIO_TRACK_SELECTED_SIGNAL]: true,
              [SUBTITLES_TRACK_SELECTED_SIGNAL]: true,
            },
          },
          ProgramInformation: {
            type: ProgramInformation,
            signals: {
              [PROGRAM_INFORMATION_CLOSED_SIGNAL]: '_handleSidePanelClose',
            },
          },
        },
        AuxiliaryButtons: {},
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._startAutoHide = _debounce(() => {
      this.hide();
      this.signal(CONTROLS_HIDE_SIGNAL);
    }, INTERFACE_DISPLAY_DURATION);

    this._index = 0;
  }

  _build() {
    // When fade in animation is finished, put Progress Bar in focus in order to properly handle Left and Right buttons
    this.tag('Main')
      .transition('alpha')
      .on('finish', () => {
        if (this.alpha < 1) {
          this.focusOnAuxiliaryControls();
        }
      });
    this._focusableItems = [this.tag('TopBar'), ...this.tag('AuxiliaryButtons').children, this.tag('Progress')];
    this._autoHideDisablingViews = [this.tag('SideGlass'), this.tag('Tracks'), this.tag('ProgramInformation')];
    this._visibleFocusableItems = [];
    this._checkFocusableItems();
    this._focusTag('Progress');
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
    this._focused = undefined;
    this._focusableItems = [];
    this._visibleFocusableItems = [];
    this._autoHideDisablingViews = [];
    this.tag('Main').transition('alpha').removeAllListeners();
    this._startAutoHide.cancel();
  }

  set isLive(value) {
    this._isLive = value;
    this.tag('SideGlass').isLiveSideGlass = value;
    this._updateSeekAvailability();
  }

  set isLiveSeekEnabled(value) {
    this._liveSeekEnabled = value;
    this._updateSeekAvailability();
  }

  /**
   * Sets an array of items visually represented by icons with textual label that allows user to interact with playback process
   */
  set topBarItems(value) {
    this.tag('TopBar').items = value;
  }

  /**
   * Sets metadata title as the first line over a Progress Bar
   */
  set title(value) {
    this.tag('Metadata').title = value;
  }

  /**
   * Sets metadata extra title as the second line over a Progress Bar
   */
  set extraTitle(value) {
    this.tag('Metadata').extraTitle = value;
  }

  /**
   * Sets info page data
   */
  set programInformation(value) {
    this.tag('Progress').storyboardId = _get(value, 'video.images.storyboard.id', null);
  }

  get isHidden() {
    return this.tag('Main').alpha === 0;
  }

  set displayOverlayBackdrop(value) {
    this.tag('Overlay').displayBackdrop = value;
  }

  set displayOverlayTop(value) {
    this.tag('Overlay').displayTop = value;
  }

  set displayOverlayBottom(value) {
    this.tag('Overlay').displayBottom = value;
  }

  set displayMetadata(value) {
    this.tag('Metadata').visible = value;
  }

  set progressBarVariant(value) {
    this.tag('Progress').variant = value;
  }

  set lBannerState(value) {
    this._lBannerState = value;
    this._updateSeekAvailability();
  }

  /**
   * Displays the controls simultaneously displaying EventMarker in a specified state
   *
   * @param {('Playing'|'Paused')} state current playback state
   */
  show(state) {
    this.tag('Toggle').show(state);
  }

  hide() {
    this.tag('Main').setSmooth('alpha', 0);
    this.focusOnAuxiliaryControls();
  }

  focusOnAuxiliaryControls() {
    const auxiliaryButton = this.tag('AuxiliaryButtons').children.find(child => child.visible);
    if (auxiliaryButton) {
      this._setIndex(this._visibleFocusableItems.indexOf(auxiliaryButton));
    } else {
      this._focusTag('Progress');
    }
    this._refocus();
  }

  showSideGlass() {
    this._focused = this.tag('SideGlass');
    this._refocus();
    sendEvent({ key: 'openSideGlass' });
  }

  showTracks() {
    this._focused = this.tag('Tracks');
    this._refocus();
    sendEvent({ key: 'playerTracksPanelOpen' });
  }

  showProgramInformation() {
    this._focused = this.tag('ProgramInformation');
    this._refocus();
    sendEvent({ key: 'pressSynopsis' });
  }

  _getFocused() {
    return this._focused;
  }

  _unfocus() {
    this.tag('Main').alpha = 0;
    this._focusTag('Progress');
  }

  _captureKey() {
    this.showControls();

    return false;
  }

  _handleLeft() {
    return true;
  }

  _handleUp() {
    if (this._index > 0) {
      this._setIndex(this._index - 1);
      const topBar = this.tag('TopBar');
      if (this._focused === topBar) {
        topBar.index = topBar.maxIndex;
      }

      return true;
    }

    return false;
  }

  _handleDown() {
    if (this._index < this._visibleFocusableItems.length - 1) {
      this._setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _setIndex(index = this._index, restartTimer = true) {
    this._index = index;
    this._focused = this._visibleFocusableItems[this._index];
    if (restartTimer) {
      this._startAutoHide();
    }
  }

  _focusTag(tagName) {
    const index = this._visibleFocusableItems.indexOf(this.tag(tagName));

    if (index === -1) {
      return;
    }

    this._setIndex(index);
  }

  _checkFocusableItems(restartTimer = true) {
    this._visibleFocusableItems = this._focusableItems.filter(item => item.visible);
    if (this._focused && !this._focused.visible) {
      this._setIndex(0, restartTimer);
    } else if (this._focused) {
      this._setIndex(this._visibleFocusableItems.indexOf(this._focused), restartTimer);
    }
  }

  _handleSidePanelClose() {
    this._focusTag('TopBar');
    this._refocus();
    this._startAutoHide();
  }

  _handleSideGlassClose() {
    this._handleSidePanelClose();
    sendEvent({ key: 'closeSideGlass' });
  }

  _handleTracksPanelClose() {
    this._handleSidePanelClose();
    sendEvent({ key: 'playerTracksPanelClose' });
  }

  _handleTopBarItemSelected(item) {
    this.signal(TOP_BAR_ITEM_SELECTED_SIGNAL, item);
    if (item && item.action) {
      switch (item.action) {
        case TOP_BAR_INFO_PAGE:
        case TOP_BAR_RELATED_VIDEOS:
        case TOP_BAR_TRACKS:
          this._startAutoHide.cancel();
          break;
        default:
          break;
      }
      Store.dispatch({ type: item.action });
    }
  }

  _updateSeekAvailability() {
    const canSeek = !this._lBannerState;
    this.tag('Progress').seekEnable = this._isLive ? this._liveSeekEnabled : canSeek;
  }

  showControls() {
    this.tag('Main').setSmooth('alpha', 1);
    if (!this._autoHideDisablingViews.includes(this._focused)) {
      this._startAutoHide();
    }
    this.signal(CONTROLS_SHOW_SIGNAL);
  }
}
